import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    Card,
    CardBody,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import { MoreHorizontal, Edit, Trash2, Inbox } from "react-feather";
import { deleteClassById, getClassesByFilter } from "../../../../../api-handle/classApi";
import DeleteConfirmationModal from "./ClassConfirmationDelete";
import { jwtDecode } from 'jwt-decode';
import Loader from "../../../../../CommonElements/Loader/loader";
import ClassDetailsModal from "./ClassDetailModal";

const ClassTable = ({ onEdit, filterDate, refresh }) => {
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState("createdAt");
    const [sortDirection, setSortDirection] = useState("desc");
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpenDetails, setIsModalOpenDetails] = useState(false);
    const [selectedClass, setSelectedClass] = useState({});
    const [classData, setClassData] = useState({})

    const toggleDropdown = (id) => {
        if (!id) return; // Guard against invalid id
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    const fetchData = async (page = 1, size = perPage, sort = sortColumn, order = sortDirection) => {
        setLoading(true);
        try {
            const queryParams = `?page=${page}&size=${size}&sort=${sort}&order=${order}&startDate=${filterDate}`;
            const res = await getClassesByFilter(queryParams);
            setClasses(res?.filteredData?.class || []);
            setTotalRows(res?.filteredData?.pagination?.totalItems || 0);
        } catch (error) {
            console.error("Error fetching classes:", error);
            setClasses([]); // Reset on error
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const decoded = jwtDecode(localStorage.getItem("access_token"));
        //decoded)
    }, [])

    useEffect(() => {
        fetchData(currentPage, perPage, sortColumn, sortDirection);
        setDropdownOpen(null); // Reset dropdown when data changes
    }, [currentPage, filterDate, refresh]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchData(page, newPerPage);
    };

    const handleSort = (column, sortDirection) => {
        setSortColumn(column.selector);
        setSortDirection(sortDirection);
        fetchData(currentPage, perPage, column.selector, sortDirection);
    };

    const handleDelete = (classItem) => {
        setIsModalOpen(true);
        setSelectedClass(classItem);
    };

    const confirmDelete = async () => {
        const success = await deleteClassById(selectedClass._id);
        if (success) {
            fetchData(currentPage); // Refetch after delete
        }
        setIsModalOpen(false);
        setSelectedClass({}); // Reset after confirmation
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedClass({}); // Reset on cancel
    };

    const closeModalDetail = () => {
        setIsModalOpenDetails(false);
        setClassData({}); // Reset on cancel
    };

    const columns = [
        {
            name: "Class Title",
            selector: (row) => row.title || "N/A",
            cell: (row) => (
                <span
                    style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline", }}
                    onClick={() => {
                        setIsModalOpenDetails(true);
                        setClassData(row);
                    }}
                >
                    {row.title || "N/A"}
                </span>
            ),
        },
        {
            name: "Class Type",
            selector: (row) => row.type || "N/A",
            cell: (row) => (
                <span style={{ textTransform: "capitalize" }}>
                    {row.type || "N/A"}
                </span>
            ),
        },
        {
            name: "Date / Time",
            selector: (row) => row.date,
            cell: (row) => {
                const rawDate = row.date;

                if (!rawDate) return "N/A";

                // If it looks like a time string (e.g., "14:30", "09:00 AM")
                const isTimeOnly = /^(\d{1,2}):(\d{2})(\s?[APMapm]{2})?$/.test(rawDate.trim());

                if (isTimeOnly) {
                    return rawDate.trim(); // Return the time string as-is
                }

                const parsed = new Date(rawDate);
                if (isNaN(parsed.getTime())) return "Invalid Date";

                // Optional: format with custom local format
                return parsed.toLocaleString(); // e.g., "6/20/2025, 2:00:00 PM"
            },
        },
        {
            name: "Schedule",
            selector: (row) => row.schedule?.join(', ') || "N/A",
            wrap: true,
            cell: (row) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {row.schedule && row.schedule.length > 0 ? (
                        row.schedule.map((day, index) => (
                            <span
                                key={index}
                                style={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1e88e5',
                                    padding: '4px 8px',
                                    borderRadius: '10px',
                                    fontSize: '10px',
                                    fontWeight: '500',
                                    textTransform: 'capitalize',
                                    border: '1px solid #bbdefb',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                }}
                            >
                                {day}
                            </span>
                        ))
                    ) : (
                        <span
                            style={{
                                backgroundColor: '#f5f5f5',
                                color: '#757575',
                                padding: '4px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            N/A
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: "Duration",
            selector: (row) => row.duration || "N/A",
            cell: (row) => `${row.duration} mins`,
        },
        {
            name: "Capacity",
            selector: (row) => row.capacity || 0,
        },
        {
            name: "Price",
            selector: (row) => row.price || 0,
            cell: (row) => `KWD ${row.price || 0}`,
        },
        {
            name: "Coach",
            selector: (row) => row.coach?.name || "N/A",
        },
        {
            name: "Aims",
            selector: (row) => row.aim?.join(', ') || "N/A",
            wrap: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <Dropdown isOpen={dropdownOpen === row._id} toggle={() => toggleDropdown(row._id)}>
                    <DropdownToggle color="light" size="sm" className="border-0 shadow-none">
                        <MoreHorizontal size={18} />
                    </DropdownToggle>
                    <DropdownMenu end>
                        <DropdownItem
                            onClick={() => onEdit(row)}
                            className="d-flex align-items-center"
                        >
                            <Edit size={14} className="me-2" /> Edit
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => handleDelete(row)}
                            className="d-flex align-items-center text-danger"
                        >
                            <Trash2 size={14} className="me-2" /> Delete
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "black",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
            },
        },
        cells: {
            style: {
                textAlign: "center",
            },
        },
    };

    const noDataComponent = (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 16px",
                textAlign: "center",
                color: "#757575",
            }}
        >
            <Inbox size={40} style={{ marginBottom: "12px", color: "#bdbdbd" }} />
            <div style={{ fontSize: "15px", fontWeight: 500, color: "#424242" }}>
                No classes found
            </div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>
                {filterDate
                    ? "No classes are scheduled for the selected date. Try a different date or clear the filter."
                    : "You haven't added any classes yet. Click \"Add Class\" to create one."}
            </div>
        </div>
    );

    return (
        <>

            <DataTable
                columns={columns}
                data={classes}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                onSort={handleSort}
                sortServer
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<Loader />}
                noDataComponent={noDataComponent}
                striped
                highlightOnHover
            />

            <DeleteConfirmationModal
                isOpen={isModalOpen}
                toggle={closeModal}
                onConfirm={confirmDelete}
            />
            <ClassDetailsModal
                isOpen={isModalOpenDetails}
                toggle={closeModalDetail}
                classData={classData}
            />
        </>
    );
};

export default ClassTable;